import { createPortal } from 'react-dom';
import { useHeaderActionsTarget } from '@/layouts/usePageTitle';
import HomeTagFilter from '@/components/dashboard/HomeTagFilter';
import { useLinkedPlantTags } from '@/hooks/useLinkedPlantTags';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  colorSchemeLightCold,

  themeQuartz,
} from 'ag-grid-community';
import { Search } from 'lucide-react';
import EntityLink from '@/components/common/EntityLink';
import ColumnValuesFilter from '@/components/common/ColumnValuesFilter';
import type { ColDef } from 'ag-grid-community';
import { fetchSensors, type Sensor } from '@/api/sensors';
import { useI18n } from '@/i18n';
import { useErrorToast } from '@/lib/notify';

const modules = [AllEnterpriseModule];
const formatReading = (value: unknown, unit: string) => value == null || !Number.isFinite(Number(value))
  ? '—' : `${Number(value).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${unit}`;
const readingColumn = (unit: string): ColDef<Sensor> => ({
  type: 'numericColumn',
  cellDataType: 'number',
  cellClass: 'ag-right-aligned-cell tabular-nums',
  headerClass: 'ag-right-aligned-header',
  filter: ColumnValuesFilter,
  filterParams: { valueLabel: (value: unknown) => formatReading(value, unit) },
  valueFormatter: ({ value }) => formatReading(value, unit),
});

const gridTheme = themeQuartz.withPart(colorSchemeLightCold).withParams({
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 11.5,
  rowHeight: 32,
  headerHeight: 32,
  borderRadius: 6,
  headerBackgroundColor: '#f8fafc',
  borderColor: '#e2e8f0',
  oddRowBackgroundColor: '#f8fafc',
});

function StatusBadge({ status }: { status: number }) {
  const { t } = useI18n();
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        status === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
      }`}
    >
      {status === 1 ? t('common.online') : t('common.offline')}
    </span>
  );
}

export default function SensorsPage() {
  const { t } = useI18n();
  const headerActionsTarget = useHeaderActionsTarget();
  const [rows, setRows] = useState<Sensor[]>([]);
  const {availableTags,selectedTagIds,setSelectedTagIds,tagFilteredRows}=useLinkedPlantTags(rows);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useErrorToast(error);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'online' | 'offline'>('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchSensors()
      .then(data => { if (!cancelled) { setRows(data); } })
      .catch(() => { if (!cancelled) setError(t('sensors.loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [t]);

  const filteredRows = useMemo(() => tagFilteredRows.filter(row => statusFilter === 'all' || (row.sensor_status === 1) === (statusFilter === 'online')), [tagFilteredRows, statusFilter]);
  const onlineCount = tagFilteredRows.filter(row => row.sensor_status === 1).length;
  const statusOptions = [
    { value: 'all', label: 'Tümü', count: tagFilteredRows.length },
    { value: 'online', label: t('common.online'), count: onlineCount },
    { value: 'offline', label: t('common.offline'), count: tagFilteredRows.length - onlineCount },
  ] as const;

  const colDefs = useMemo<ColDef<Sensor>[]>(
    () => [
      {
        field: 'sensor_name',
        headerName: t('sensors.sensor'),
        flex: 1.4,
        minWidth: 220,
        pinned: 'left',
        filter: ColumnValuesFilter,
        cellRenderer:({data,value}:{data?:Sensor;value?:string})=><div className="flex h-full min-w-0 items-center gap-2">
          {data?.sensor_uuid_id?<Link to={`/dashboard/sensors/${data.sensor_uuid_id}`} className="min-w-0 truncate font-semibold text-blue-600 hover:text-blue-800 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" title={value}>{value || '—'}</Link>:<span className="min-w-0 truncate">{value || '—'}</span>}
          {data && <span className="ml-auto inline-flex shrink-0 items-center gap-1">
            {[{label:'F',name:'FTP',active:data.sensor_ftp_connection_status===1},{label:'M',name:'Modbus',active:data.sensor_modbus_connection_status===1}].map(connection=><span key={connection.label} aria-label={`${connection.name}: ${connection.active?'Güncel veri var':'Güncel veri yok'}`} title={`${connection.name}: ${connection.active?'Son 31 dakika içinde veri alındı':'Son 31 dakika içinde veri alınmadı'}`} className={`inline-flex h-5 w-5 items-center justify-center rounded text-[10px] font-bold ${connection.active?'bg-emerald-100 text-emerald-700':'bg-rose-100 text-rose-700'}`}>{connection.label}</span>)}
          </span>}
        </div>,
      },
      {
        field: 'sensor_power_plant_name',
        headerName: t('sensors.powerPlant'),
        flex: 1.6,
        minWidth: 190,
        filter: ColumnValuesFilter,
        cellRenderer: ({ data, value }: { data?: Sensor; value?: string }) => (
          <EntityLink to={data?.sensor_power_plant_uuid_id ? `/dashboard/powerplants/${data.sensor_power_plant_uuid_id}` : null}>{value}</EntityLink>
        ),
      },
      {
        field: 'sensor_plant_count',
        headerName: 'Santral Sayısı',
        type: 'numericColumn',
        cellDataType: 'number',
        cellClass: 'ag-right-aligned-cell tabular-nums',
        width: 125,
        filter: ColumnValuesFilter,
      },
      {
        field: 'sensor_brand',
        headerName: t('sensors.brand'),
        flex: 1,
        minWidth: 130,
        filter: ColumnValuesFilter,
        valueFormatter: ({ value }: { value: string | null }) => value || '—',
      },
      {
        field: 'sensor_model',
        headerName: t('sensors.model'),
        flex: 1,
        minWidth: 130,
        filter: ColumnValuesFilter,
        valueFormatter: ({ value }: { value: string | null }) => value || '—',
      },
      {
        field: 'sensor_serial_number',
        headerName: t('sensors.serial'),
        flex: 1.2,
        minWidth: 150,
        filter: ColumnValuesFilter,
        valueFormatter: ({ value }: { value: string | null }) => value || '—',
      },
      {
        ...readingColumn('°C'),
        field: 'sensor_pv_temp',
        headerName: t('sensors.pvTemp'),
        width: 120,
      },
      {
        ...readingColumn('°C'),
        field: 'sensor_amp_temp',
        headerName: t('sensors.ambientTemp'),
        width: 120,
      },
      {
        ...readingColumn('W/m²'),
        field: 'sensor_radiation',
        headerName: t('sensors.radiation'),
        width: 120,
      },
      {
        field: 'sensor_last_seen',
        headerName: t('common.lastSeen'),
        flex: 1.2,
        minWidth: 160,
        filter: ColumnValuesFilter,
        valueFormatter: ({ value }: { value: string | null }) => value || '—',
      },
      {
        field: 'sensor_status',
        headerName: t('common.status'),
        width: 120,
        filter: ColumnValuesFilter,
        filterValueGetter: params => params.data?.sensor_status === 1 ? t('common.online') : t('common.offline'),
        getQuickFilterText: params => params.value === 1 ? t('common.online') : t('common.offline'),
        cellRenderer: ({ value }: { value: number }) => (
          <StatusBadge status={value} />
        ),
      },
    ],
    [t],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      floatingFilter: false,
      suppressHeaderMenuButton: false,
    }),
    [],
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      {headerActionsTarget && createPortal(<div className="flex w-full min-w-0 flex-wrap items-center justify-start gap-1.5 xl:justify-end">
        <label className="flex items-center h-8 gap-1.5 rounded-lg border border-slate-200 px-2 text-slate-400"><Search size={14} /><input aria-label="Sensör listesinde ara" placeholder="Sensör, santral veya seri numarası ara…" value={search} onChange={event => setSearch(event.target.value)} className="w-32 bg-transparent text-xs text-slate-700 outline-none xl:w-40" /></label>
        <div role="group" aria-label="Sensör durumu" className="flex h-8 items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-50 p-0.5">{statusOptions.map(option => <button key={option.value} type="button" aria-pressed={statusFilter === option.value} onClick={() => setStatusFilter(option.value)} className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold transition ${statusFilter === option.value ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}>{option.label}<span className={`rounded px-1 text-[10px] tabular-nums ${statusFilter === option.value ? 'bg-white/20' : 'bg-slate-200/60 text-slate-500'}`}>{option.count}</span></button>)}</div>
        <HomeTagFilter tags={availableTags} selectedIds={selectedTagIds} onChange={setSelectedTagIds} title="Bağlı santral etiketleri" allLabel="Tüm sensörler göster" emptyLabel="Tüm sensörler"/>

      </div>,headerActionsTarget)}


      <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="min-h-0 flex-1 [&_.ag-paging-row-summary-panel]:hidden">
        <AgGridReact<Sensor>
          modules={modules}
          theme={gridTheme}
          rowData={filteredRows}
          quickFilterText={search}
          getRowId={params => `${params.data.sensor_power_plant_uuid_id}:${params.data.sensor_uuid_id}`}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          loading={loading}
          animateRows
          enableCellTextSelection
          pagination
          paginationPageSize={100}
          paginationPageSizeSelector={[25, 50, 100]}
          tooltipShowDelay={250}
          overlayNoRowsTemplate="<span>Bu filtrelere uygun sensör bulunamadı.</span>"
        />
        </div>
      </section>
    </div>
  );
}
